/**
 * The site's shared document head, declared once (ΩΞ-REBUILD-001-A7 F10, F11).
 *
 * The application shell (index.html) and the hand-written static pages under public/ used to carry
 * their own copies of the brand icon links and share metadata — and drifted: "/" (intro.html) had no
 * description, Open Graph or Twitter tags at all, and nine static pages had no icon link. A page now
 * writes a marker where the shared block belongs and the build expands it (vite.config.ts,
 * `m618-shared-head`), so every served document carries the same bytes from this one declaration.
 *
 *   <!-- m618:head icons -->   the favicon and the brand icon
 *   <!-- m618:head share -->   description, Open Graph and Twitter card of the site itself
 *
 * The strings are plain data with no browser or Node dependency: the app shell (pageMeta.ts) and
 * the build configuration both import them.
 */

export const SITE_TITLE = 'MUTATIO618 — Exit The Matrix';
export const SITE_DESCRIPTION =
  'MUTATIO618 — Truth through transformation. Mind, body, and soul sovereignty through ancient wisdom and modern technology. Veritas in Mutatio.';
export const SITE_SHARE_DESCRIPTION =
  'Truth through transformation. Mind, body, and soul sovereignty through ancient wisdom and modern technology.';
export const SITE_SHARE_IMAGE = 'https://mutatio618.com/assets/logos/logo-full-red.png';
/** The brand icon asset. /favicon.ico is built from it by scripts/favicon.mjs. */
export const SITE_ICON = '/assets/logos/icon-cube-red.png';
export const SITE_FAVICON = '/favicon.ico';

export const HEAD_BLOCKS = {
  icons: [
    `<link rel="icon" href="${SITE_FAVICON}" sizes="16x16 32x32 48x48" />`,
    `<link rel="icon" type="image/png" href="${SITE_ICON}" />`,
    `<link rel="apple-touch-icon" href="${SITE_ICON}" />`,
  ],
  share: [
    `<meta name="description" content="${SITE_DESCRIPTION}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:title" content="${SITE_TITLE}" />`,
    `<meta property="og:description" content="${SITE_SHARE_DESCRIPTION}" />`,
    `<meta property="og:image" content="${SITE_SHARE_IMAGE}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${SITE_TITLE}" />`,
    `<meta name="twitter:description" content="${SITE_SHARE_DESCRIPTION}" />`,
    `<meta name="twitter:image" content="${SITE_SHARE_IMAGE}" />`,
  ],
} as const satisfies Record<string, readonly string[]>;

export type HeadBlock = keyof typeof HEAD_BLOCKS;

const MARKER = /^([ \t]*)<!--\s*m618:head\s+([a-z-]+)\s*-->[ \t]*$/gm;
const ANY_MARKER = /m618:head/;

/**
 * Replaces every `<!-- m618:head <block> -->` line with its block, one tag per line at the marker's
 * indentation. A document without markers is returned byte-identical. An unknown block name throws:
 * a typo must fail the build, not ship a page without its icon.
 */
export function expandHeadMarkers(html: string): string {
  const expanded = html.replace(MARKER, (_line, indent: string, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(HEAD_BLOCKS, name)) {
      throw new Error(`unknown shared head block "${name}"`);
    }
    return HEAD_BLOCKS[name as HeadBlock].map((tag) => indent + tag).join('\n');
  });
  if (ANY_MARKER.test(expanded)) throw new Error('a shared head marker is malformed (not alone on its line?)');
  return expanded;
}
